import { datadogRum } from '@datadog/browser-rum';
import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';
import SubheadingText from '@ui/atoms/SubheadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import ReadMore from '@ui/molecules/ReadMore';
import RoundedCard from '@ui/molecules/RoundedCard';

import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import { confirmAppointment } from '@utils/confirmAppointment';
import { exists } from '@utils/general';

import theme from '@theme/FolxTheme';

const SpacedHeader = spaced(HeadingText);
const SpacedCard = spaced(RoundedCard);
const SpacedWYSIWYG = spaced(WYSIWYGContent);
const PaddedSpacedWYSIWYG = padded(SpacedWYSIWYG);
const SpacedSkeleton = spaced(Skeleton);

const EditButton = styled(Button)`
	width: fit-content;
`;

const CancellationPolicy = spaced(styled(ReadMore)`
	padding: ${theme.spacing.smmd};
`);

interface IScheduledAppointment {
	id: string;
	time: DateTime;
}

export interface IConfirmedPaymentNotRequiredStepProps {
	pageContent: IConfirmedPaymentNotRequiredStepContent;
	existingFormData?: Record<string, unknown>;
	handleResetStatus: () => void;
	isIrlAppointment?: boolean;
	appointmentNotConfirmedPath?: string;
	errorPath?: string;
}

// content specified in member-appointment-selection butter page
export interface IConfirmedPaymentNotRequiredStepContent {
	appointmentHeader: string;
	cancellationHeader?: string;
	cancellationDescription?: string;
	interpreterInfo: string;
	editLabel: string;
	appointmentSubheader: string;
	appointmentDescription?: string;
	locationSubheader: string;
	locationDescription: string;
	continueLabel: string;
}

const SkeletonComponent = () => (
	<div data-testid="skeleton">
		<CenteredGridItem>
			<SpacedSkeleton height="md" width="xxxl" marginBottom="xs" />
			<SpacedSkeleton height="lgxl" fullWidth marginBottom="xs" />
			<SpacedSkeleton height="xs" width="xxxxxl" marginLeft="xs" />
			<SpacedSkeleton height="xxxl" fullWidth marginTop="sm" />
		</CenteredGridItem>
	</div>
);

export const ConfirmedPaymentNotRequiredStep = ({
	existingFormData = {},
	isIrlAppointment,
	pageContent,
	handleResetStatus,
	appointmentNotConfirmedPath = '../not_confirmed',
	errorPath = '../error',
}: IConfirmedPaymentNotRequiredStepProps) => {
	// Initial setup
	const [loading, setLoading] = useState(true);
	const location = useLocation();

	// Appointment setup
	const scheduledAppointment: IScheduledAppointment | undefined = useMemo(() => {
		if (['cogsworthId', 'cogsworthAppointmentTime'].every((key) => key in existingFormData)) {
			return {
				id: existingFormData.cogsworthId as string,
				time: DateTime.fromISO(existingFormData.cogsworthAppointmentTime as string),
			};
		} else if (exists(location?.state?.cogsworthId) && exists(location?.state?.apptDate)) {
			return {
				id: location?.state?.cogsworthId,
				time: DateTime.fromISO(location?.state?.apptDate as string),
			};
		}
	}, [existingFormData, location]);

	// Appointment confirmation
	useEffect(() => {
		async function verifyAppointmentConfirmation(): Promise<void> {
			if (!scheduledAppointment?.id) {
				datadogRum.addError(
					`Error confirming appointment: ${JSON.stringify(
						Error('Missing appointment info: scheduledAppointment seems to be empty')
					)}`
				);
				navigate(errorPath);
			}
			const confirmation = await confirmAppointment(scheduledAppointment?.id ?? '').catch((e) => {
				datadogRum.addError(`Error confirming appointment: ${JSON.stringify(e)}`);
				navigate(errorPath);
			});

			if (confirmation) {
				setLoading(false);
			} else {
				navigate(appointmentNotConfirmedPath);
			}
		}

		verifyAppointmentConfirmation();
	}, [appointmentNotConfirmedPath, scheduledAppointment?.id]);

	const appointmentDate = scheduledAppointment?.time;
	const localizedDate = appointmentDate?.toFormat('EEEE, MMMM d');
	const localizedTime = appointmentDate?.toFormat('h:mm a ZZZZ');

	return (
		<CenteredGridItem>
			<LoadingWrapper loading={loading} skeleton={<SkeletonComponent />}>
				<SpacedHeader marginBottom="sm">{pageContent.appointmentHeader}</SpacedHeader>
				{exists(pageContent.cancellationHeader) && exists(pageContent.cancellationDescription) && (
					<CancellationPolicy marginBottom="sm" label={pageContent.cancellationHeader}>
						<WYSIWYGContent
							dangerouslySetInnerHTML={{ __html: pageContent.cancellationDescription ?? '' }}
						/>
					</CancellationPolicy>
				)}
				<PaddedSpacedWYSIWYG
					dangerouslySetInnerHTML={{ __html: pageContent.interpreterInfo }}
					textVariant="fineprint"
					marginBottom="lgxl"
					paddingRight="md"
				/>

				<SpacedCard
					bgColor="beige"
					marginBottom="lgxl"
					footerMargin="smmd"
					footer={
						<EditButton
							label={pageContent.editLabel}
							labelSize="xs"
							onClick={() => handleResetStatus()}
						/>
					}>
					<FlexColumn gap="xssm">
						<BasicText variant="fineprintBold">{pageContent.appointmentSubheader}</BasicText>
						<SubheadingText>{`${localizedDate} at ${localizedTime}`}</SubheadingText>
						{exists(pageContent.appointmentDescription) && (
							<BasicText variant="fineprint">{pageContent.appointmentDescription}</BasicText>
						)}
						{isIrlAppointment && (
							<>
								<BasicText variant="fineprintBold">{pageContent.locationSubheader}</BasicText>
								<WYSIWYGContent
									dangerouslySetInnerHTML={{ __html: pageContent.locationDescription }}
								/>
							</>
						)}
					</FlexColumn>
				</SpacedCard>
				<Button
					variant="secondary"
					label={pageContent.continueLabel}
					onClick={() => navigate('/app/member/appointments')}
				/>
			</LoadingWrapper>
		</CenteredGridItem>
	);
};
